/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    SupervisorAccount as SupervisorAccountIcon,
    HowToReg as HowToRegIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import moment from 'moment';
import Teachr from 'src/Objects/models/Teachr';
import { capitalizeFirstLetter, createTeachrAddress, prepareTeachrForUpdate, updateTeachrData } from 'src/Utils/Function';
import { useProfilContext } from '../../../../Contexts/ProfilContexte';
import { userSetSapForm } from '../../../../Redux/Actions/UserActions';
import ButtonField from '../../../../Components/Ui/ButtonField';
import Settings from '../../../../Components/Settings';
import FormInput from '../../../../Components/Ui/FormInput';
import FormDate from '../../../../Components/FormFields/FormDate';
import AddressDetailed, { AddressFields, AddressErrors } from '../../../../Components/Ui/AddressDetailed';
import DialogLayout from '../../../../Components/DialogLayout';
import useTailwindBrand from '../../../../Theme/useTailwindBrand';
import { DECLARONS_REQUIRED_APE_CODES, REQUIRED_APE_CODES } from '../FirstCompletProfil';
import { ENV_DECLARONS, isBrandActive } from '../../../../config/environment.config';

type Civilite = 'M.' | 'Mme';

interface SapForm {
    civilite: Civilite | '';
    nom: string;
    prenom: string;
    dateNaissance: string;
    villeNaissance: string;
    codeApe: string;
    dateCreation: string;
    telephone: string;
    email: string;
    siret: string;
}

interface DirectorForm {
    isSelf: boolean | null;
    civilite: Civilite | '';
    nom: string;
    prenom: string;
    telephone: string;
}

type SapErrors = Partial<Record<keyof SapForm, string>>;
type DirectorErrors = Partial<Record<keyof Omit<DirectorForm, 'isSelf'>, string>>;

const validateSapForm = (form: SapForm): SapErrors => {
    const errors: SapErrors = {};
    if (!form.civilite) errors.civilite = 'La civilité est requise.';
    if (!form.nom.trim()) errors.nom = 'Le nom est requis.';
    if (!form.prenom.trim()) errors.prenom = 'Le prénom est requis.';
    if (!form.dateNaissance.trim()) errors.dateNaissance = 'La date de naissance est requise.';
    if (!form.villeNaissance.trim()) errors.villeNaissance = 'La ville de naissance est requise.';
    const validApeCodes = isBrandActive(ENV_DECLARONS) ? DECLARONS_REQUIRED_APE_CODES : REQUIRED_APE_CODES;
    if (!form.codeApe.trim() || (validApeCodes.includes(form.codeApe.trim()) ? false : form.codeApe.trim().length !== 5)) errors.codeApe = 'Le code APE doit contenir exactement 5 caractères.';
    if (!form.dateCreation.trim()) errors.dateCreation = 'La date de création est requise.';
    if (!/^(\+33|0)[1-9](\d{2}){4}$/.test(form.telephone.replace(/\s/g, ''))) errors.telephone = 'Numéro de téléphone invalide.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Adresse email invalide.';
    return errors;
};

const validateDirectorForm = (dir: DirectorForm): DirectorErrors => {
    const errors: DirectorErrors = {};
    if (!dir.civilite) errors.civilite = 'La civilité est requise.';
    if (!dir.nom.trim()) errors.nom = 'Le nom est requis.';
    if (!dir.prenom.trim()) errors.prenom = 'Le prénom est requis.';
    if (!/^(\+33|0)[1-9](\d{2}){4}$/.test(dir.telephone.replace(/\s/g, ''))) errors.telephone = 'Numéro de téléphone invalide.';
    return errors;
};

const validateAddressFields = (addr: AddressFields): AddressErrors => {
    const errors: AddressErrors = {};
    if (!addr.libelleVoie.trim()) errors.libelleVoie = 'Le libellé de voie est requis.';
    if (!addr.codePostal.trim() || !/^\d{5}$/.test(addr.codePostal.trim())) errors.codePostal = 'Code postal invalide (5 chiffres requis).';
    if (!addr.ville.trim()) errors.ville = 'La ville est requise.';
    return errors;
};

interface SapProcurationFormProps {
    onNext: () => void;
    onPrevious: () => void;
}

const SapProcurationForm: React.FC<SapProcurationFormProps> = ({ onNext, onPrevious }) => {
    const { teachr, currentProcuration, handleRefreshSiret } = useProfilContext();

    const dispatch = useDispatch();
    const { brandConfig } = useTailwindBrand();
    const siretInfo = useSelector((state: any) => state.user?.siretInfo?.etablissement);
    const sapFormData = useSelector((state: any) => state.user?.sapFormData);
    const primaryHex = brandConfig.colors?.primary ?? '#1976d2';
    const isAE = teachr?.businessType?.toUpperCase() === 'AE';

    useEffect(() => { handleRefreshSiret && handleRefreshSiret() }, [handleRefreshSiret]);

    useEffect(() => {
        if (!siretInfo) return;
        setForm(prev => ({
            ...prev,
            codeApe: prev.codeApe || siretInfo?.uniteLegale?.activitePrincipaleUniteLegale || '',
            dateCreation: prev.dateCreation || siretInfo?.dateCreationEtablissement || '',
            siret: prev.siret || siretInfo?.siret || '',
        }));
        setAddressFields(prev => ({
            ...prev,
            numeroVoie: prev.numeroVoie || siretInfo?.adresseEtablissement?.numeroVoieEtablissement || '',
            lettreVoie: prev.lettreVoie || siretInfo?.adresseEtablissement?.indiceRepetitionEtablissement || '',
            typeVoie: prev.typeVoie || siretInfo?.adresseEtablissement?.typeVoieEtablissement || '',
            libelleVoie: prev.libelleVoie || siretInfo?.adresseEtablissement?.libelleVoieEtablissement || '',
            lieuDit: prev.lieuDit || siretInfo?.adresseEtablissement?.lieuDitEtablissement || '',
            complement: prev.complement || siretInfo?.adresseEtablissement?.complementEtablissement || '',
            codePostal: prev.codePostal || siretInfo?.adresseEtablissement?.codePostalEtablissement || '',
            ville: prev.ville || siretInfo?.adresseEtablissement?.libelleCommuneEtablissement || '',
        }));
    }, [siretInfo]);

    const [form, setForm] = useState<SapForm>({
        civilite: sapFormData?.civilite || (teachr?.sex === 'M' ? 'M.' : teachr?.sex === 'F' ? 'Mme' : ''),
        nom: capitalizeFirstLetter(sapFormData?.nom) || capitalizeFirstLetter(teachr?.lastName) || '',
        prenom: capitalizeFirstLetter(sapFormData?.prenom) || capitalizeFirstLetter(teachr?.firstName) || '',
        dateNaissance: sapFormData?.dateNaissance || (teachr?.birthday ? moment(teachr.birthday).format('YYYY-MM-DD') : ''),
        villeNaissance: sapFormData?.villeNaissance || teachr?.bornPlace || '',
        codeApe: sapFormData?.codeApe || siretInfo?.uniteLegale?.activitePrincipaleUniteLegale || '',
        dateCreation: sapFormData?.dateCreation || siretInfo?.dateCreationEtablissement || '',
        telephone: sapFormData?.telephone || teachr?.phone || '',
        email: sapFormData?.email || teachr?.mail || '',
        siret: sapFormData?.siret || siretInfo?.siret,
    });
    const [errors, setErrors] = useState<SapErrors>({});

    const [addressFields, setAddressFields] = useState<AddressFields>({
        numeroVoie: teachr?.address?.streetNumber || sapFormData?.address?.numeroVoie || siretInfo?.adresseEtablissement?.numeroVoieEtablissement || '',
        lettreVoie: sapFormData?.address?.lettreVoie || siretInfo?.adresseEtablissement?.indiceRepetitionEtablissement || '',
        typeVoie: sapFormData?.address?.typeVoie || siretInfo?.adresseEtablissement?.typeVoieEtablissement || '',
        libelleVoie: teachr?.address?.streetName || sapFormData?.address?.libelleVoie || siretInfo?.adresseEtablissement?.libelleVoieEtablissement || '',
        lieuDit: sapFormData?.address?.lieuDit || siretInfo?.adresseEtablissement?.lieuDitEtablissement || '',
        complement: sapFormData?.address?.complement || siretInfo?.adresseEtablissement?.complementEtablissement || '',
        codePostal: teachr?.address?.zip || sapFormData?.address?.codePostal || siretInfo?.adresseEtablissement?.codePostalEtablissement || '',
        ville: teachr?.address?.city || sapFormData?.address?.ville || siretInfo?.adresseEtablissement?.libelleCommuneEtablissement || '',
    });
    const [addressErrors, setAddressErrors] = useState<AddressErrors>({});

    const [director, setDirector] = useState<DirectorForm>({
        isSelf: sapFormData?.director?.isSelf ?? null,
        civilite: sapFormData?.director?.civilite || '',
        nom: sapFormData?.director?.nom || '',
        prenom: sapFormData?.director?.prenom || '',
        telephone: sapFormData?.director?.telephone || '',
    });
    const [directorErrors, setDirectorErrors] = useState<DirectorErrors>({});
    const [directorIsSelfError, setDirectorIsSelfError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [submitted, setSubmitted] = useState(false);

    const handleChange = (field: keyof SapForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setForm(prev => ({ ...prev, [field]: value }));
        if (submitted) {
            setErrors(prev => ({ ...prev, [field]: validateSapForm({ ...form, [field]: value })[field] }));
        }
    };

    const handleCivilite = (_: any, value: Civilite | null) => {
        if (!value) return;
        setForm(prev => ({ ...prev, civilite: value }));
        if (submitted) setErrors(prev => ({ ...prev, civilite: undefined }));
    };

    const handleDateNaissance = (value: any) => {
        let dateValue = '';
        if (value) {
            if (typeof value === 'object' && value.format) dateValue = value.format('YYYY-MM-DD');
            else if (typeof value === 'string') dateValue = value;
            else if (value instanceof Date) dateValue = moment(value).format('YYYY-MM-DD');
        }
        if (dateValue) {
            setForm(prev => ({ ...prev, dateNaissance: dateValue }));
            if (submitted) setErrors(prev => ({ ...prev, dateNaissance: undefined }));
        }
    };

    const handleDateCreation = (value: any) => {
        let dateValue = '';
        if (value) {
            if (typeof value === 'object' && value.format) dateValue = value.format('YYYY-MM-DD');
            else if (typeof value === 'string') dateValue = value;
            else if (value instanceof Date) dateValue = moment(value).format('YYYY-MM-DD');
        }
        if (dateValue) {
            setForm(prev => ({ ...prev, dateCreation: dateValue }));
            if (submitted) setErrors(prev => ({ ...prev, dateCreation: undefined }));
        }
    };

    const handleAddressChange = (fields: AddressFields) => {
        setAddressFields(fields);
        if (submitted) setAddressErrors(validateAddressFields(fields));
    };

    const handleDirectorChange = (field: keyof Omit<DirectorForm, 'isSelf'>) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDirector(prev => ({ ...prev, [field]: value }));
        if (submitted) {
            setDirectorErrors(prev => ({ ...prev, [field]: validateDirectorForm({ ...director, [field]: value })[field] }));
        }
    };

    const handleDirectorCivilite = (_: any, value: Civilite | null) => {
        if (!value) return;
        setDirector(prev => ({ ...prev, civilite: value }));
        if (submitted) setDirectorErrors(prev => ({ ...prev, civilite: undefined }));
    };

    const handleIsSelf = (_: any, value: boolean | null) => {
        if (value === null) return;
        setDirector(prev => ({ ...prev, isSelf: value }));
        setDirectorIsSelfError(false);
    };

    const handleCreateAddress = async (): Promise<string | null> => {
        if (teachr && teachr.id) {
            try {
                setLoading(true);
                const updatedTeachr = { ...teachr } as Teachr;

                const addressIri = (addressFields && teachr.id && teachr['@id'])
                    ? await createTeachrAddress(addressFields, teachr['@id'] as string, teachr.id)
                    : null;

                if (addressIri) updatedTeachr.address = addressIri;
                if (form.villeNaissance) updatedTeachr.bornPlace = form.villeNaissance;

                prepareTeachrForUpdate(updatedTeachr, {});
                await updateTeachrData(updatedTeachr, dispatch, setLoading);
                onNext();
            } catch (err) {
                console.warn('Erreur lors de l\'upload des documents:', err);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        }
        return null;
    }
    const handleUpdate = async () => {
        if (teachr && teachr?.address) {
            if (teachr.address?.streetNumber !== addressFields.numeroVoie ||
                teachr.address?.streetName !== addressFields.libelleVoie ||
                teachr.address?.postalCode !== addressFields.codePostal ||
                teachr.address?.city !== addressFields.ville) {
                handleCreateAddress();
            } else {
                onNext();
            }
        } else {
            handleCreateAddress();
        }
    }

    const handleClickContinue = () => {
        setSubmitted(true);
        const newErrors = currentProcuration === 40 ? {} : validateSapForm(form);
        const newAddressErrors = validateAddressFields(addressFields);
        setErrors(newErrors);
        setAddressErrors(newAddressErrors);

        let newDirectorErrors: DirectorErrors = {};
        if (!isAE) {
            if (director.isSelf === null) {
                setDirectorIsSelfError(true);
                return;
            }
            if (director.isSelf === false) {
                newDirectorErrors = validateDirectorForm(director);
                setDirectorErrors(newDirectorErrors);
            }
        }

        if (
            Object.keys(newErrors).length === 0 &&
            Object.keys(newAddressErrors).length === 0 &&
            Object.keys(newDirectorErrors).length === 0
        ) {
            setConfirmOpen(true);
        }
    };

    const handleSubmit = () => {
        setConfirmOpen(false);
        dispatch(userSetSapForm({
            ...form,
            address: addressFields,
            director: isAE ? null : director,
        }));
        handleUpdate();
    };

    const sectionBoxSx = {
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
    };

    const sectionHeaderSx = {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 2.5,
    };

    const toggleButtonSx = (selected: boolean, error?: boolean) => ({
        px: 2.5,
        fontFamily: brandConfig.fontFamily,
        fontWeight: 600,
        borderRadius: '8px !important',
        border: `1px solid ${error ? '#dc2626' : '#d1d5db'} !important`,
        '&.Mui-selected': {
            bgcolor: `${primaryHex}15`,
            borderColor: `${primaryHex} !important`,
            color: primaryHex,
        },
    });

    const CiviliteToggle = ({
        value,
        onChange,
        error,
    }: {
        value: Civilite | '';
        onChange: (e: any, v: Civilite | null) => void;
        error?: string;
    }) => (
        <div>
            <p
                className={`text-sm font-semibold mb-1.5 ${error ? 'text-red-600' : 'text-gray-600'}`}
                style={{ fontFamily: brandConfig.fontFamily }}
            >
                Civilité {error && <span className="text-red-600">*</span>}
            </p>
            <ToggleButtonGroup value={value || null} exclusive onChange={onChange} size="small" sx={{ gap: 1 }}>
                <ToggleButton value="M." sx={toggleButtonSx(value === 'M.', !!error)}>
                    M.
                </ToggleButton>
                <ToggleButton value="Mme" sx={toggleButtonSx(value === 'Mme', !!error)}>
                    Mme
                </ToggleButton>
            </ToggleButtonGroup>
            {error && (
                <p className="text-xs text-red-600 mt-1 italic font-semibold" style={{ fontFamily: brandConfig.fontFamily }}>
                    {error}
                </p>
            )}
        </div>
    );

    return (
        <Settings
            title={currentProcuration === 40 ? "Déclaration SAP" : "Procuration SAP"}
            subtitle={currentProcuration === 40 ? "Renseignez l’adresse de votre entreprise afin que nous puissions établir des factures en votre nom et pur votre compte" : "Renseignez vos informations pour établir votre procuration"}
            icon={<AssignmentIcon sx={{ color: primaryHex, fontSize: 28 }} />}
            height="100%"
        >
            {/* Section informations personnelles */}
            {currentProcuration !== 40 && (
                <Grid item xs={12}>
                    <Box sx={sectionBoxSx}>
                        <Box sx={sectionHeaderSx}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: '#e0f2fe',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <PersonIcon sx={{ color: '#0284c7', fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                    Informations personnelles
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                    Vos informations d'identité
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <CiviliteToggle value={form.civilite} onChange={handleCivilite} error={errors.civilite} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput label="Nom" type="text" value={form.nom} onChange={handleChange('nom')} error={errors.nom} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput label="Prénom" type="text" value={form.prenom} onChange={handleChange('prenom')} error={errors.prenom} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDate
                                    label="Date de naissance"
                                    callback={handleDateNaissance}
                                    defaultValue={form.dateNaissance}
                                    required
                                    error={errors.dateNaissance}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Ville de naissance"
                                    type="text"
                                    placeholder="Ex : Paris"
                                    value={form.villeNaissance}
                                    onChange={handleChange('villeNaissance')}
                                    error={errors.villeNaissance}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            )}

            {/* Section adresse */}
            <Grid item xs={12}>
                <Box sx={sectionBoxSx}>
                    <Box sx={sectionHeaderSx}>
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: '#f0fdf4',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <LocationOnIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                Adresse de l'établissement
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                Adresse principale de votre activité professionnelle
                            </Typography>
                        </Box>
                    </Box>
                    <AddressDetailed value={addressFields} onChange={handleAddressChange} errors={addressErrors} />
                </Box>
            </Grid>

            {/* Section informations de l'établissement */}
            {currentProcuration !== 40 && (
                <Grid item xs={12}>
                    <Box sx={sectionBoxSx}>
                        <Box sx={sectionHeaderSx}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: `${primaryHex}1a`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <BusinessIcon sx={{ color: primaryHex, fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                    Informations de l'établissement
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                    Données de votre entreprise
                                </Typography>
                            </Box>
                        </Box>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Code APE"
                                    type="text"
                                    placeholder="Ex : 8559B"
                                    value={form.codeApe}
                                    onChange={handleChange('codeApe')}
                                    error={errors.codeApe}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormDate
                                    label="Date de création de l'entreprise"
                                    callback={handleDateCreation}
                                    defaultValue={form.dateCreation}
                                    required
                                    error={errors.dateCreation}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Téléphone de l'établissement"
                                    type="tel"
                                    placeholder="Ex : 06 12 34 56 78"
                                    value={form.telephone}
                                    onChange={handleChange('telephone')}
                                    error={errors.telephone}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Adresse email"
                                    type="email"
                                    value={form.email}
                                    onChange={handleChange('email')}
                                    error={errors.email}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            )}

            {/* Section dirigeant (uniquement si pas AE) */}
            {!isAE && (
                <Grid item xs={12}>
                    <Box sx={sectionBoxSx}>
                        <Box sx={sectionHeaderSx}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    bgcolor: '#fef9c3',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <SupervisorAccountIcon sx={{ color: '#ca8a04', fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="700" fontFamily={brandConfig.fontFamily} color="text.primary">
                                    Dirigeant de l'entreprise
                                </Typography>
                                <Typography variant="caption" color="text.secondary" fontFamily={brandConfig.fontFamily}>
                                    Indiquez si vous êtes le dirigeant ou renseignez ses coordonnées
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ mb: 2.5 }}>
                            <Typography
                                variant="subtitle2"
                                fontWeight="600"
                                fontFamily={brandConfig.fontFamily}
                                color="text.primary"
                                sx={{ mb: 1.5 }}
                            >
                                Êtes-vous le dirigeant de l'entreprise ?
                            </Typography>
                            <ToggleButtonGroup
                                value={director.isSelf}
                                exclusive
                                onChange={handleIsSelf}
                                size="small"
                                sx={{ gap: 1 }}
                            >
                                <ToggleButton
                                    value={true}
                                    sx={{
                                        px: 3,
                                        fontFamily: brandConfig.fontFamily,
                                        fontWeight: 600,
                                        borderRadius: '8px !important',
                                        border: `1px solid ${directorIsSelfError ? '#dc2626' : '#d1d5db'} !important`,
                                        '&.Mui-selected': {
                                            bgcolor: '#f0fdf4',
                                            borderColor: '#16a34a !important',
                                            color: '#16a34a',
                                        },
                                    }}
                                >
                                    Oui
                                </ToggleButton>
                                <ToggleButton
                                    value={false}
                                    sx={{
                                        px: 3,
                                        fontFamily: brandConfig.fontFamily,
                                        fontWeight: 600,
                                        borderRadius: '8px !important',
                                        border: `1px solid ${directorIsSelfError ? '#dc2626' : '#d1d5db'} !important`,
                                        '&.Mui-selected': {
                                            bgcolor: `${primaryHex}15`,
                                            borderColor: `${primaryHex} !important`,
                                            color: primaryHex,
                                        },
                                    }}
                                >
                                    Non
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {directorIsSelfError && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ display: 'block', mt: 0.5, fontFamily: brandConfig.fontFamily, fontStyle: 'italic', fontWeight: 600 }}
                                >
                                    Veuillez indiquer si vous êtes le dirigeant.
                                </Typography>
                            )}
                        </Box>

                        {director.isSelf === true && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1.5,
                                    bgcolor: '#f0fdf4',
                                    border: '1px solid #bbf7d0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                }}
                            >
                                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
                                <Typography variant="body2" fontWeight="600" color="#166534" fontFamily={brandConfig.fontFamily}>
                                    Vos informations personnelles seront utilisées pour le dirigeant.
                                </Typography>
                            </Box>
                        )}

                        {director.isSelf === false && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <CiviliteToggle
                                        value={director.civilite}
                                        onChange={handleDirectorCivilite}
                                        error={directorErrors.civilite}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Nom du dirigeant"
                                        type="text"
                                        value={director.nom}
                                        onChange={handleDirectorChange('nom')}
                                        error={directorErrors.nom}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Prénom du dirigeant"
                                        type="text"
                                        value={director.prenom}
                                        onChange={handleDirectorChange('prenom')}
                                        error={directorErrors.prenom}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormInput
                                        label="Téléphone du dirigeant"
                                        type="tel"
                                        placeholder="Ex : 06 12 34 56 78"
                                        value={director.telephone}
                                        onChange={handleDirectorChange('telephone')}
                                        error={directorErrors.telephone}
                                    />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </Grid>
            )}

            {/* Boutons d'action */}
            <Grid item xs={12}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        flexDirection: { xs: 'column', lg: 'row' },
                        gap: 2,
                        pt: 1,
                    }}
                >
                    <ButtonField
                        label="Précédent"
                        variant="outlined"
                        className="w-full font-bold lg:w-auto"
                        onClick={onPrevious}
                    />
                    <ButtonField
                        label="Continuer"
                        variant="contained"
                        className="w-full font-bold lg:w-auto"
                        onClick={handleClickContinue}
                        startIcon={<HowToRegIcon />}
                        loading={loading}
                    />
                </Box>
            </Grid>

            <DialogLayout
                open={confirmOpen}
                handleClose={() => setConfirmOpen(false)}
                title="Confirmer vos informations"
                text={`Êtes-vous sûr${teachr?.sex === " MAN" ? "" : "e"} de toutes les informations renseignées ? Une fois passé à l'étape suivante, il ne sera plus possible de revenir en arrière.`}
                cancel="Annuler"
                valid="Valider"
                handleSubmit={handleSubmit}
            />
        </Settings>
    );
};

export default SapProcurationForm;
